import { Theme } from "@material-ui/core";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { FormState } from "final-form";
import React from "react";
import { Field, FieldProps, FormSpy } from "react-final-form";
import { Schema } from "../../dataAccess/Schema";
import { TextFieldComponent } from "../Inputs/TextFieldComponent";
import { Label } from "./Label";
import {
  DynamicField,
  Field as SchemaField,
  OnChangeCallback,
  Size
} from "../../dataAccess/Field";
import { LoggerFactory } from "../../logger";
import { SecuredComponentProps } from "../Secured";
import { inject, observer } from "mobx-react";
import { reduce, get } from "lodash";

const log = LoggerFactory.create("DynamicForm");

export class FormFields extends Schema<DynamicFormField, FormField> {}

export type DynamicFormField = DynamicField & {};
export type FormField = SchemaField & {};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: "grid",
      gridTemplateColumns: "100%"
    },
    gridFieldWrapper: {
      display: "grid",
      gridTemplateColumns: "35% 65%",
      minHeight: 70
    },
    fullWidthFieldWrapper: {
      display: "grid",
      gridTemplateRows: "1fr auto"
    },
    gridField: {
      paddingRight: theme.spacing(5)
    },
    fullWidthField: {
      paddingLeft: theme.spacing(1) * 5,
      paddingRight: theme.spacing(1) * 5,
      paddingBottom: theme.spacing(1),
      float: "left",
      width: "100%",
      alignSelf: "center",
      minHeight: 70
    }
  });

export interface DynamicFormState<T> {
  model: T;
}

export interface DynamicFormProps<T>
  extends FieldProps<any, any>,
    WithStyles<typeof styles> {
  fields: FormFields;
  model?: T;
  Presentation: React.FunctionComponent<PresentationComponentProps>;
  onChange: (formState: FormState<any>) => void;
  values: any;
}

export interface PresentationComponentProps {
  fieldMapper: (fields: FormField[]) => any;
  data: any;
  schema: FormFields;
}

type DynamicFieldWithOnChange = DynamicField & { onChange: OnChangeCallback };
type DynamicFieldWithOnInit = DynamicField & { onInit: OnChangeCallback };

interface OnChangeCallbackMap {
  [field: string]: OnChangeCallback;
}

@inject("security")
@observer
class DynamicFormComponent<T> extends React.Component<
  DynamicFormProps<T> & SecuredComponentProps,
  DynamicFormState<T>
> {
  public onChangeCallbackMap: OnChangeCallbackMap;
  public onInitCallbackMap: OnChangeCallbackMap;
  public fieldValuesCache: {};

  public constructor(props: DynamicFormProps<T>) {
    super(props);
    this.state = { model: props.model || ({} as T) };
    this.onChangeCallbackMap = {};
    this.onInitCallbackMap = {};
    this.fieldValuesCache = {};
    this.fillFuncQueue(this.props.fields.fields);
  }

  public render() {
    const {
      classes,
      handleSubmit,
      Presentation,
      fields,
      values,
      security,
      requiredAccess
    } = this.props;

    const finalRequiredAccess = requiredAccess || "editable";
    const disableForm =
      !security || (security && !security[finalRequiredAccess]());

    return (
      <form onSubmit={handleSubmit} className={classes.root}>
        <FormSpy
          onChange={this.onChange}
          subscription={{ modified: true, values: true, active: true }}
        />
        <Presentation
          fieldMapper={this.fieldMapper}
          schema={disableForm ? fields.makeAllReadonly() : fields}
          data={values}
        />
        {this.props.children}
      </form>
    );
  }

  private fillFuncQueue(fields: DynamicField[]) {
    this.onChangeCallbackMap = fields
      .filter(
        (field: DynamicField): field is DynamicFieldWithOnChange =>
          field.onChange !== undefined
      )
      .reduce(
        (callbackMap: OnChangeCallbackMap, field: DynamicFieldWithOnChange) => {
          callbackMap[field.name] = field.onChange;
          return callbackMap;
        },
        {} as OnChangeCallbackMap
      );
    this.onInitCallbackMap = fields
      .filter(
        (field: DynamicField): field is DynamicFieldWithOnInit =>
          field.onInit !== undefined
      )
      .reduce(
        (callbackMap: OnChangeCallbackMap, field: DynamicFieldWithOnInit) => {
          callbackMap[field.name] = field.onInit;
          return callbackMap;
        },
        {} as OnChangeCallbackMap
      );
  }

  private firstChange = true;
  private onChange = (formState: FormState<Object>) => {
    log.debug("form state changed", formState);

    if (this.firstChange) {
      // ignoring first change event
      // due to error in final-form spy implementation which is triggering onChange during render
      this.firstChange = false;
      return;
    }

    const initFields = {} as { [key: string]: true };

    const modifiedFields = reduce(
      formState.modified,
      (acc, v, k) => {
        if (v) {
          acc.push(k);
        } else if (
          this.fieldValuesCache[k] === undefined &&
          formState.values &&
          get(formState.values, k) !== undefined
        ) {
          initFields[k] = true;
          acc.push(k);
        }
        return acc;
      },
      [] as string[]
    );
    let anyValueChanged = false;
    modifiedFields.forEach(modifiedField => {
      const onChangeCallback = this.onChangeCallbackMap[modifiedField];
      const onInitCallback = this.onInitCallbackMap[modifiedField];
      const oldValue = this.fieldValuesCache[modifiedField];

      // formState.values === undefined when deleting object from db
      const newValue =
        formState.values !== undefined
          ? get(formState.values, modifiedField)
          : undefined;
      const valueChanged = oldValue !== newValue;

      if (valueChanged) {
        anyValueChanged = true;
        this.fieldValuesCache[modifiedField] = newValue;

        if (initFields[modifiedField]) {
          if (onInitCallback) {
            onInitCallback(newValue, oldValue, formState);
          }
        } else {
          if (onChangeCallback) {
            onChangeCallback(newValue, oldValue, formState);
          }
        }
      }
    });
    if (anyValueChanged) {
      this.props.onChange(formState);
    }
  };

  private fieldMapper = (fields: FormField[]) => {
    return fields.map(
      (field, idx) => {
        const fieldProps = this.prepareComponentProps(field, false);

        return (
          <div
            key={field.name + idx}
            className={
              field.renderSize === Size.BIG
                ? this.props.classes.fullWidthFieldWrapper
                : this.props.classes.gridFieldWrapper
            }
          >
            <Label field={field} />
            <div
              className={
                field.renderSize === Size.BIG
                  ? this.props.classes.fullWidthField
                  : this.props.classes.gridField
              }
            >
              <Field {...fieldProps} />
            </div>
          </div>
        );
      },
      { data: this.props.values }
    );
  };

  private prepareComponentProps(field: FormField, forceReadonly: boolean) {
    return {
      values: this.props.values,
      key: field.name,
      name: field.name,
      component: forceReadonly ? TextFieldComponent : field.type.Component,
      validate: field.type.valid,
      disabled: forceReadonly ? true : field.readonly,
      readOnly: forceReadonly ? true : field.readonly,
      ...field.type.props
    } as FieldProps<any, any>;
  }
}

export const DynamicForm = withStyles(styles, { withTheme: true })(
  DynamicFormComponent
);
